import React, { createContext, useContext, useState, useEffect } from 'react';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if we're using the build-time constants
  const isUsingBuildTimeConfig = typeof window !== 'undefined' && 
                              typeof window.__APP_NAME__ !== 'undefined';
  console.log('Using build-time configuration:', isUsingBuildTimeConfig);
  
  // Fix for empty features in window.__APP_FEATURES__
  if (isUsingBuildTimeConfig && typeof window.__APP_FEATURES__ === 'object') {
    console.log('Fixing empty features object in window.__APP_FEATURES__');
    window.__APP_FEATURES__ = {
      ...window.__APP_FEATURES__,
      courses: true,
      navbar: true
    };
    console.log('Updated features:', window.__APP_FEATURES__);
  }
  
  // Safe JSON parse function
  const safeJsonParse = (jsonString, fallback) => {
    try {
      if (typeof jsonString === 'string') {
        return JSON.parse(jsonString);
      } else if (typeof jsonString === 'object') {
        return jsonString;
      }
      return fallback;
    } catch (e) {
      console.error('Failed to parse JSON:', e);
      return fallback;
    }
  };
  
  // Initialize with build-time constants if available
  const [appConfig, setAppConfig] = useState(() => {
    if (isUsingBuildTimeConfig) {
      console.log('Initializing with build-time constants');
      console.log('Build-time logo URL:', window.__APP_LOGO_URL__);
      console.log('All window variables:', {
        name: window.__APP_NAME__,
        primaryColor: window.__APP_PRIMARY_COLOR__,
        secondaryColor: window.__APP_SECONDARY_COLOR__,
        logoUrl: window.__APP_LOGO_URL__,
        features: window.__APP_FEATURES__,
        navigation: window.__APP_NAVIGATION__
      });
      try {
        return {
          name: window.__APP_NAME__,
          primaryColor: window.__APP_PRIMARY_COLOR__,
          secondaryColor: window.__APP_SECONDARY_COLOR__,
          logoUrl: window.__APP_LOGO_URL__,
          features: safeJsonParse(window.__APP_FEATURES__, {}),
          navigation: safeJsonParse(window.__APP_NAVIGATION__, [])
        };
      } catch (e) {
        console.error('Error initializing with build-time constants:', e);
        return null;
      }
    }
    return null;
  });

  // Fetch runtime config only if build-time config is not available
  useEffect(() => {
    const fetchAppConfig = async () => {
      // Skip if we already have build-time config
      if (isUsingBuildTimeConfig) return;
      
      try {
        setLoading(true);
        console.log('Fetching runtime configuration...');
        // Get the current domain
        const domain = window.location.hostname;
        
        // Fetch app configuration from the backend
        const response = await fetch(`/api/apps/config?domain=${domain}`, {
          headers: {
            'X-App-Domain': domain
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch app configuration');
        }

        const data = await response.json();
        
        if (!data.success || !data.data) {
          throw new Error('Invalid app configuration');
        }

        console.log('Received runtime configuration:', data.data);
        setAppConfig(prev => ({
          ...prev,
          ...data.data,
          primaryColor: data.data.primary_color,
          secondaryColor: data.data.secondary_color
        }));
        setError(null);
      } catch (err) {
        console.error('Error fetching app configuration:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (!isUsingBuildTimeConfig) {
      fetchAppConfig();
    } else {
      setLoading(false);
    }
  }, [isUsingBuildTimeConfig]);

  // Function to update specific app settings
  const updateAppSettings = async (settings) => {
    if (!appConfig) return;

    // Skip update if using build-time config (would need a rebuild anyway)
    if (isUsingBuildTimeConfig) {
      console.warn('Cannot update app settings when using build-time configuration');
      return false;
    }

    try {
      const response = await fetch(`/api/apps/${appConfig.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-App-Domain': window.location.hostname
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        throw new Error('Failed to update app settings');
      }

      const data = await response.json();
      setAppConfig(prev => ({ ...prev, ...data.data }));
      return true;
    } catch (err) {
      console.error('Error updating app settings:', err);
      return false;
    }
  };

  const value = {
    appConfig,
    loading,
    error,
    updateAppSettings,
    getAppName: () => {
      console.log('Current appConfig:', appConfig);
      return appConfig?.name || 'SaltMarsh App';
    },
    getLogo: () => {
      console.log('Using logo URL:', appConfig?.logoUrl || appConfig?.logo_url);
      return appConfig?.logoUrl || appConfig?.logo_url || 'default-logo.png';
    },
    getPrimaryColor: () => appConfig?.primaryColor || '#3498db',
    getSecondaryColor: () => appConfig?.secondaryColor || '#2ecc71',
    // Feature flags and UI configuration
    hasFeature: (featureName) => {
      // Always enable certain critical features
      if (featureName === 'courses' || featureName === 'navbar') {
        console.log(`Force enabling critical feature: ${featureName}`);
        return true;
      }
      
      // Default features that should always be on
      const defaultFeatures = {
        navbar: true,
        courses: true  // Add courses as a default feature
      };
      
      if (!appConfig?.features) {
        // Return default if defined, otherwise false
        return defaultFeatures[featureName] || false;
      }
      
      // Return from app config, falling back to defaults
      return appConfig.features[featureName] !== undefined 
        ? !!appConfig.features[featureName]
        : (defaultFeatures[featureName] || false);
    },
    getNavItems: () => appConfig?.navigation || [],
    getFooterTabs: () => {
      // Runtime config uses footer_tabs, build-time doesn't have this
      return appConfig?.footer_tabs || [];
    }
  };

  if (loading) {
    // You might want to render a loading component here
    return <div>Loading app configuration...</div>;
  }

  if (error) {
    // You might want to render an error component here
    return <div>Error loading app configuration: {error}</div>;
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}; 